import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { ScoreType } from "@modules/scoreboard/scoreTypes"
import { getScoreTypesArray } from "@modules/scoreboard/scoreUtils"
import { ExerciseFormikType } from "@modules/sessionBlock/exercises/exerciseFormikTypes"
import { useSessionFormContext } from "@modules/sessions/hooks/useSessionFormContext"
import { FormikInput } from "@modules/ui/components/formikInput"
import { HUText } from "@modules/ui/components/huText"
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch"
import { useEffect, useMemo, useState } from "react"
import { useTheme } from "styled-components"

export const ScoreBloc: React.FC = () => {
  const { formik, updatedBlockIndex } = useSessionFormContext()
  const theme = useTheme()

  const formikNameBase = `blocks[${updatedBlockIndex}].content`
  const formikValue =
    updatedBlockIndex !== null ? (formik.values.blocks[updatedBlockIndex].content as ExerciseFormikType) : null

  const [isScore, setIsScore] = useState(formikValue?.scoreType !== ScoreType.NO_SCORE)
  const scoreTypes = useMemo(() => getScoreTypesArray(), [])

  useEffect(() => {
    setIsScore(formikValue?.scoreType !== ScoreType.NO_SCORE)
  }, [formikValue?.scoreType])

  if (updatedBlockIndex === null) return <></>

  return (
    <div className="card flex flex-column flex-1 mb-0 py-3 px-4 gap-3">
      {/* Ask a score */}
      <div className="flex flex-column gap-2">
        <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
          {word("program.planning.create.exercise.scoreboard").toUpperCase()}
        </HUText>
        <div className="flex w-full justify-content-between mb-1">
          <HUText className="my-1" fontStyle="LL">
            {word("program.planning.create.exercise.askScore.titleBloc")}
          </HUText>
          <InputSwitch
            checked={isScore}
            onChange={(e: InputSwitchChangeEvent) => {
              if (e.value === false) {
                formik.setFieldValue(`${formikNameBase}.scoreType`, ScoreType.NO_SCORE)
              }
              setIsScore(e.value ?? false)
            }}
          />
        </div>
        {isScore && (
          <FormikInput
            formikStyle={{ flexGrow: 1 }}
            getFieldProps={formik.getFieldProps}
            name={`${formikNameBase}.scoreType`}
            setFieldValue={formik.setFieldValue}
            isInvalid={isFormFieldInValid(`${formikNameBase}.scoreType`, formik)}
            error={getFormErrorMessage(`${formikNameBase}.scoreType`, formik)}
            type="dropdown"
            dropdownProps={{
              value: formikValue?.scoreType,
              options: scoreTypes,
            }}
          />
        )}
        <HUText fontStyle="BS" color={theme.neutralColor600}>
          {word("program.planning.create.exercise.askScore.descriptionBloc")}
        </HUText>
      </div>

      {/* Media requirement */}
      <div className="flex flex-column mt-3 gap-2">
        <HUText type="label" color={theme.neutralColor700} fontStyle="LS">
          {word("program.planning.create.exercise.media_requirement.title").toUpperCase()}
        </HUText>

        <div className="flex w-full justify-content-between">
          <HUText className="my-1" fontStyle="LL">
            {word("program.planning.create.exercise.media_requirement.label")}
          </HUText>
          <InputSwitch
            checked={formikValue?.requiredMedia ?? false}
            onChange={(e: InputSwitchChangeEvent) => {
              formik.setFieldValue(`${formikNameBase}.requiredMedia`, e.value ?? false)
            }}
          />
        </div>

        <HUText fontStyle="BS" color={theme.neutralColor600} className="mt-1">
          {word("program.planning.create.exercise.media_requirement.description")}
        </HUText>
      </div>
    </div>
  )
}
